import React, { useEffect, useState } from "react";
import { Button, Tabs, Modal, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import AddAdminModal from "./AddAdminModal";
import EditAdminModal from "./EditAdminModal";
import {
  getAdminList,
  deleteAdmin,
  getAuditLogs,
} from "../../../services/AdminPortalAPIs";
import Papa from "papaparse";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledTable from "../../../components/reusable/StyledTable";
import {
  getAdminTableColumns,
  auditLogColumns,
  roles,
  permissions,
  permissionsMap,
  User,
  LogEntry,
} from "./SettingsColumn";
import StyledInput from "src/components/reusable/StyledInput";

const { TabPane } = Tabs;

const AdminSettings: React.FC = () => {
  const [admins, setAdmins] = useState<User[]>([]);
  const [, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<User | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [hasFetchedLogs, setHasFetchedLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("1");
  const didFetchRef = React.useRef(false);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await getAdminList();
      if (res.data.status && Array.isArray(res.data.data)) {
        const adminsFromApi: User[] = res.data.data.map(
          ([name, email, role, lastActive]: [
            string,
            string,
            string,
            string | null
          ]) => ({
            name,
            email,
            role,
            lastActive: lastActive
              ? new Date(lastActive).toLocaleString()
              : "Never",
          })
        );
        setAdmins(adminsFromApi);
      } else {
        message.error("Failed to load admins properly");
      }
    } catch {
      message.error("Failed to fetch admins");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await getAuditLogs();
      if (res.data.status && Array.isArray(res.data.logs)) {
        const logsFromApi: LogEntry[] = res.data.logs.map((log: any) => ({
          admin: `${log.user_name} (${log.role})`,
          action: log.action,
          details: log.details,
          timestamp: log.timestamp,
        }));
        setLogs(logsFromApi);
      } else {
        message.error("Failed to load logs properly");
      }
    } catch {
      message.error("Failed to fetch logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDelete = (email: string) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `Are you sure you want to delete admin: ${email}?`,
      onOk: async () => {
        try {
          await deleteAdmin({ target_email: email });
          message.success("Admin deleted");
          fetchAdmins();
        } catch {
          message.error("Failed to delete admin");
        }
      },
    });
  };

  const handleExportLogs = () => {
    if (!logs.length) {
      message.warning("No logs to export.");
      return;
    }
    const csv = Papa.unparse(logs);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `audit_logs_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;
    fetchAdmins();
  }, []);

const filteredAdmins = React.useMemo(() =>
  admins.filter(
    admin =>
      admin.name.toLowerCase().includes(searchText.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchText.toLowerCase())
  ),
  [admins, searchText]
);

  return (
    <div className="admin-portal-page ">
      <span className="heading-primary">Admin Settings</span>
      <div className="filters-settings-container">
 <StyledInput
  placeholder="Search admin..."
  aria-label="Search admin"
  onChange={e => setSearchText(e.target.value)}
  className="admin-search"
/>

  {activeTab === "1" ? (
    <button
      className="button button--primary"
      onClick={() => setIsAddOpen(true)}
      aria-label="Add Admin"
    >
      + Add Admin
    </button>
  ) : activeTab === "2" ? (
    <Button
      icon={<DownloadOutlined />}
      onClick={handleExportLogs}
      className="button button--primary"
      disabled={logsLoading}
    >
      Export Log
    </Button>
  ) : null}
</div>

      <Tabs
        defaultActiveKey="1"
        onChange={key => {
          setActiveTab(key);
          if (key === "2" && !hasFetchedLogs) {
            fetchLogs();
            setHasFetchedLogs(true);
          }
        }}
      >
        <TabPane tab="Admin Management" key="1">
          <StyledTable
            columns={getAdminTableColumns(setEditingAdmin, handleDelete)}
            dataSource={filteredAdmins}
            rowKey={record => record.email}
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
              position: ["bottomCenter"],
            }}
            className="pipeline-table"
            locale={{ emptyText: "No admins found" }}
          />

          <div className="headline">Role Permissions</div>

          <StyledTable
            className="campaign-table pipeline-table"
            dataSource={permissions.map(perm => ({
              key: perm,
              permission: perm,
              ...roles.reduce((acc, role) => {
                acc[role] = permissionsMap[role].includes(perm) ? "✓" : "";
                return acc;
              }, {} as Record<string, string>),
            }))}
            columns={[
              {
                title: "Permission",
                dataIndex: "permission",
                key: "permission",
              },
              ...roles.map(role => ({
                title: role,
                dataIndex: role,
                key: role,
              })),
            ]}
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
              position: ["bottomCenter"],
            }}
          />
        </TabPane>

        <TabPane tab="Admin Action Logs" key="2">
          <StyledTable
            className="pipeline-table"
            columns={auditLogColumns}
            dataSource={logsLoading ? [] : logs}
            rowKey={(_, index) => index?.toString() || ""}
            pagination={{
              pageSize: 6,
              showSizeChanger: false,
              position: ["bottomCenter"],
            }}
            locale={{
              emptyText: logsLoading ? <LoadingSpinner /> : "No logs found",
            }}
          />
        </TabPane>
      </Tabs>

      <AddAdminModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={fetchAdmins}
      />
      {editingAdmin && (
        <EditAdminModal
          admin={editingAdmin}
          open={!!editingAdmin}
          onClose={() => setEditingAdmin(null)}
          onSuccess={() => {
            fetchAdmins();
            setEditingAdmin(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminSettings;
